import { usePositionsMappedToSalesforceOpportunityLineItemIds } from '@/clients/position/usePositionsMappedToSalesforceOpportunityLineItemIds';
import { useMonolithFrontendUrl } from '@/shared/hooks/useMonolithFrontendUrl';
import { getPositionOverviewMonolithRoute } from '@/shared/utils/positionRoutes';
import { EnhancedTooltip, Link } from '@schuettflix/react-components';
import { useTranslation } from 'react-i18next';

type DealLineItemPositionReferenceProps = {
    salesforceOpportunityId: string;
    salesforceOpportunityLineItemId: string;
};

export function DealLineItemPositionReference({
    salesforceOpportunityId,
    salesforceOpportunityLineItemId,
}: DealLineItemPositionReferenceProps) {
    const { data: positionMaps } = usePositionsMappedToSalesforceOpportunityLineItemIds(salesforceOpportunityId);
    const positions =
        positionMaps.find(
            positionMap => positionMap.salesforceOpportunityLineItemId === salesforceOpportunityLineItemId
        )?.positions ?? [];
    const { url } = useMonolithFrontendUrl({
        target: 'currentTab',
        historyMethod: 'push',
    });
    const { t } = useTranslation();

    const createPositionOverviewMonolithRoute = (position: { constructionProjectId: string; id: string }) =>
        url + getPositionOverviewMonolithRoute(position.constructionProjectId, position.id);

    if (!positions.length) {
        return null;
    }

    if (positions.length === 1 && positions[0].id) {
        return (
            <Link
                className="font-copy-md-strong whitespace-nowrap"
                href={createPositionOverviewMonolithRoute(positions[0])}
                is="text"
                label={t('salesforceDealImporter.openInSFLX')}
                size="md"
                underline
            />
        );
    }

    return (
        <EnhancedTooltip
            closeOn="mouseleave"
            delayOnClose={500}
            delayOnOpen={0}
            position="top"
            size="md"
            trigger={
                <Link
                    is="text"
                    className="font-copy-md-strong whitespace-nowrap"
                    label={t('salesforceDealImporter.openInSFLX')}
                    size="md"
                    underline
                />
            }
        >
            <div className="font-copy-sm flex flex-col gap-y-2">
                <span>{t('salesforceDealImporter.linkedPositions')}</span>
                {positions.map(position => (
                    <Link
                        key={position.id}
                        className="font-copy-sm-strong"
                        href={createPositionOverviewMonolithRoute(position)}
                        is="text"
                        label={position.positionNumber}
                        size="md"
                    />
                ))}
            </div>
        </EnhancedTooltip>
    );
}
